import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, LayoutGrid, Rows, Plus, Settings, Briefcase } from "lucide-react";
import { listarProjetos, Projeto } from "../services/projetos";
import { listarEventosCalendario, EventoCalendario, TipoEvento, EVENTO_VISUAL } from "../services/calendario";
import { EventoModal, ConfigCalendarioModal, FiltroTipoChip, TIPOS_LISTA } from "./Calendario";
import { useAuth } from "../hooks/useAuth";

type TipoMarco = "inicio" | "primeiraEntrega" | "prazoFeedback" | "feedback" | "entregaFinal" | "inicioSuporte" | "fimSuporte";

interface MarcoProjeto {
  tipo: TipoMarco;
  data: Date;
  label: string;
  emoji: string;
  cor: string;
  projeto: Projeto;
}

const ESTILO_MARCO: Record<TipoMarco, { label: string; emoji: string; cor: string }> = {
  inicio:          { label: "Início",         emoji: "▶",  cor: "bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600" },
  primeiraEntrega: { label: "1ª entrega",     emoji: "📤", cor: "bg-purple-100 text-purple-700 border-purple-300 dark:bg-purple-900/40 dark:text-purple-300 dark:border-purple-700" },
  prazoFeedback:   { label: "Prazo feedback", emoji: "⏳", cor: "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-700" },
  feedback:        { label: "Validado",       emoji: "✅", cor: "bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-700" },
  entregaFinal:    { label: "Entrega final",  emoji: "🎯", cor: "bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-700" },
  inicioSuporte:   { label: "Início suporte", emoji: "🛟", cor: "bg-cyan-100 text-cyan-700 border-cyan-300 dark:bg-cyan-900/40 dark:text-cyan-300 dark:border-cyan-700" },
  fimSuporte:      { label: "Fim suporte",    emoji: "🏁", cor: "bg-slate-200 text-slate-700 border-slate-400 dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600" },
};

// Parseia a parte de DATA (yyyy-mm-dd) como meia-noite LOCAL. Datas do backend
// vêm como ISO UTC ("...T00:00:00.000Z"); usar new Date() direto desloca um dia
// em fusos negativos (UTC-3), jogando o evento pro dia anterior na grade.
function parseDataLocal(s: string): Date {
  const [y, m, d] = s.slice(0, 10).split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

function marcosDoProjeto(p: Projeto): MarcoProjeto[] {
  const marcos: MarcoProjeto[] = [];
  const add = (tipo: TipoMarco, dataStr: string | null | undefined) => {
    if (!dataStr) return;
    const meta = ESTILO_MARCO[tipo];
    marcos.push({ tipo, data: parseDataLocal(dataStr), label: meta.label, emoji: meta.emoji, cor: meta.cor, projeto: p });
  };
  add("inicio", p.dataInicio);
  add("primeiraEntrega", (p as any).dataPrimeiraEntrega);
  add("prazoFeedback", (p as any).prazoFeedback);
  add("feedback", (p as any).feedbackRecebidoEm);
  add("entregaFinal", p.prazoEntrega);
  add("inicioSuporte", (p as any).inicioSuporte);
  add("fimSuporte", (p as any).fimSuporte);
  return marcos;
}

function ymd(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function diasNoMes(ano: number, mes0: number) {
  return new Date(ano, mes0 + 1, 0).getDate();
}

/** Se cair em sáb/dom, avança pra próxima segunda. */
function ajustarParaDiaUtil(d: Date): Date {
  const dow = d.getDay();
  if (dow === 6) return new Date(d.getFullYear(), d.getMonth(), d.getDate() + 2);
  if (dow === 0) return new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
  return d;
}

type View = "dia" | "semana" | "mes";
type Modo = "linha" | "grade";
/** "" = tudo · "projetos" = só marcos de projeto · resto = um tipo de evento da empresa. */
type Filtro = "" | "projetos" | TipoEvento;

type MarcoExibicao = MarcoProjeto & { dataExibicao: Date; ajustado: boolean };

const KEY_VISAO = "calendario_visao";

/**
 * Cronograma: calendário ÚNICO da agência (jul/2026 — "juntar tudo no mesmo
 * filtro"). Mostra lado a lado, no mesmo dia:
 *  - MARCOS DE PROJETO (início, 1ª entrega, prazo de feedback, entrega final…),
 *    derivados das datas do projeto e empurrados pro próximo dia útil (↻);
 *  - EVENTOS DA EMPRESA (aniversário, feriado, reunião, treinamento, podcast…),
 *    que gestor/admin cria e edita aqui mesmo.
 * O modo "Linha" (Gantt) é por projeto, então só desenha marcos — ao filtrar por
 * um tipo de evento da empresa a tela volta pro modo Grade sozinha.
 * A aba Entregas (pauta por cliente) segue separada, em CalendarioHub.
 */
export function Cronograma() {
  const hoje = new Date();
  const qc = useQueryClient();
  const { can } = useAuth();
  const podeGerenciar = can(["gestor", "admin"]);

  const [view, setView] = useState<View>(() => {
    const v = localStorage.getItem(KEY_VISAO);
    return v === "dia" || v === "semana" || v === "mes" ? v : "mes";
  });
  function setViewPersist(v: View) {
    setView(v);
    try { localStorage.setItem(KEY_VISAO, v); } catch {}
  }
  const [modo, setModo] = useState<Modo>(() => {
    const saved = localStorage.getItem("calendario_modo");
    return (saved === "linha" || saved === "grade") ? saved : "grade";
  });
  function setModoPersist(m: Modo) {
    setModo(m);
    try { localStorage.setItem("calendario_modo", m); } catch {}
  }
  const [referencia, setReferencia] = useState(new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate()));
  const [filtro, setFiltro] = useState<Filtro>("");
  const [editando, setEditando] = useState<EventoCalendario | null>(null);
  const [criando, setCriando] = useState<{ data: string } | null>(null);
  const [showConfig, setShowConfig] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["projetos-calendario"],
    queryFn: () => listarProjetos({ perPage: 200 }),
  });
  const projetos = (data?.projetos as Projeto[]) || [];

  // Range da LINHA (Gantt): exatamente o período escolhido, sem padding.
  const { inicio, fim, totalDias, label } = useMemo(() => {
    if (view === "dia") {
      const i = new Date(referencia.getFullYear(), referencia.getMonth(), referencia.getDate());
      const f = new Date(i.getFullYear(), i.getMonth(), i.getDate(), 23, 59, 59);
      return {
        inicio: i,
        fim: f,
        totalDias: 1,
        label: i.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" }),
      };
    }
    if (view === "semana") {
      const dow = referencia.getDay();
      const i = new Date(referencia.getFullYear(), referencia.getMonth(), referencia.getDate() - dow);
      const f = new Date(i.getFullYear(), i.getMonth(), i.getDate() + 6, 23, 59, 59);
      return {
        inicio: i,
        fim: f,
        totalDias: 7,
        label: `${i.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })} – ${f.toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" })}`,
      };
    }
    const i = new Date(referencia.getFullYear(), referencia.getMonth(), 1);
    const td = diasNoMes(i.getFullYear(), i.getMonth());
    const f = new Date(i.getFullYear(), i.getMonth(), td, 23, 59, 59);
    return {
      inicio: i,
      fim: f,
      totalDias: td,
      label: i.toLocaleDateString("pt-BR", { month: "long", year: "numeric" }),
    };
  }, [view, referencia]);

  // Range da GRADE: no mês inclui os dias de padding das semanas das pontas.
  // Fica aqui em cima porque a busca de eventos usa o mesmo intervalo desenhado.
  const dias = useMemo(() => {
    if (view === "dia") {
      return [new Date(referencia.getFullYear(), referencia.getMonth(), referencia.getDate())];
    }
    if (view === "semana") {
      const dow = referencia.getDay();
      const ini = new Date(referencia.getFullYear(), referencia.getMonth(), referencia.getDate() - dow);
      return Array.from({ length: 7 }, (_, i) => new Date(ini.getFullYear(), ini.getMonth(), ini.getDate() + i));
    }
    const primeiro = new Date(referencia.getFullYear(), referencia.getMonth(), 1);
    const ultimo = new Date(referencia.getFullYear(), referencia.getMonth() + 1, 0);
    const ini = new Date(primeiro.getFullYear(), primeiro.getMonth(), primeiro.getDate() - primeiro.getDay());
    const f = new Date(ultimo.getFullYear(), ultimo.getMonth(), ultimo.getDate() + (6 - ultimo.getDay()));
    const total = Math.round((f.getTime() - ini.getTime()) / 86400000) + 1;
    return Array.from({ length: total }, (_, i) => new Date(ini.getFullYear(), ini.getMonth(), ini.getDate() + i));
  }, [referencia, view]);

  const primeiroDia = dias[0];
  const ultimoDia = dias[dias.length - 1];

  const { data: eventos = [] } = useQuery({
    queryKey: ["calendario-eventos", ymd(primeiroDia), ymd(ultimoDia)],
    queryFn: () => listarEventosCalendario(primeiroDia, ultimoDia),
  });

  const mostrarMarcos = filtro === "" || filtro === "projetos";

  // Eventos da empresa por dia, já filtrados pelo chip ativo.
  const eventosPorDia = useMemo(() => {
    const m = new Map<string, EventoCalendario[]>();
    if (filtro === "projetos") return m;
    for (const ev of eventos) {
      if (filtro && ev.tipo !== filtro) continue;
      const arr = m.get(ev.data) || [];
      arr.push(ev);
      m.set(ev.data, arr);
    }
    for (const arr of m.values()) arr.sort((a, b) => (a.hora || "00:00").localeCompare(b.hora || "00:00"));
    return m;
  }, [eventos, filtro]);

  // Marcos por dia (indexados pela data já empurrada pro dia útil).
  const marcosPorDia = useMemo(() => {
    const mapa = new Map<string, MarcoExibicao[]>();
    if (!mostrarMarcos) return mapa;
    for (const p of projetos) {
      for (const mk of marcosDoProjeto(p)) {
        const ajustada = ajustarParaDiaUtil(mk.data);
        const k = ymd(ajustada);
        const arr = mapa.get(k) || [];
        arr.push({ ...mk, dataExibicao: ajustada, ajustado: ajustada.getTime() !== mk.data.getTime() });
        mapa.set(k, arr);
      }
    }
    return mapa;
  }, [projetos, mostrarMarcos]);

  const projetosVisiveis = useMemo(() => {
    return projetos
      .filter((p) => {
        // parseDataLocal (não new Date()) — senão o UTC-3 puxa a data pro dia
        // anterior e o projeto some do Gantt no mês em que ele começa/entrega.
        const ini = parseDataLocal(p.dataInicio);
        // Sem prazoEntrega: usa a própria dataInicio como fim do range pra avaliação.
        const fimP = p.prazoEntrega ? parseDataLocal(p.prazoEntrega) : ini;
        return fimP >= inicio && ini <= fim;
      })
      .sort((a, b) => parseDataLocal(a.dataInicio).getTime() - parseDataLocal(b.dataInicio).getTime());
  }, [projetos, inicio, fim]);

  function navegar(delta: number) {
    const d = new Date(referencia);
    if (view === "dia") d.setDate(d.getDate() + delta);
    else if (view === "semana") d.setDate(d.getDate() + delta * 7);
    else d.setMonth(d.getMonth() + delta);
    setReferencia(d);
  }
  function irParaHoje() {
    setReferencia(new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate()));
  }

  /**
   * Filtro e modo têm que combinar nos DOIS sentidos: o Gantt é por projeto, então
   * não sabe desenhar evento da empresa. Filtrou evento estando na Linha → cai pra
   * Grade; entrou na Linha com evento filtrado → o filtro volta pra "Projetos"
   * (senão o chip fica aceso mostrando uma coisa e a tela mostrando outra).
   */
  function trocarFiltro(f: Filtro) {
    const novo = filtro === f ? "" : f;
    setFiltro(novo);
    if (novo && novo !== "projetos" && modo === "linha") setModoPersist("grade");
  }
  function trocarModo(m: Modo) {
    setModoPersist(m);
    if (m === "linha" && filtro && filtro !== "projetos") setFiltro("projetos");
  }

  const hojeIso = ymd(hoje);

  function tituloColuna(diaIdx: number) {
    const dt = new Date(inicio.getFullYear(), inicio.getMonth(), inicio.getDate() + diaIdx);
    if (view === "mes") return String(dt.getDate());
    return dt.toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "short" });
  }
  function isHojeColuna(diaIdx: number) {
    const dt = new Date(inicio.getFullYear(), inicio.getMonth(), inicio.getDate() + diaIdx);
    return ymd(dt) === hojeIso;
  }
  function isWeekendColuna(diaIdx: number) {
    const dt = new Date(inicio.getFullYear(), inicio.getMonth(), inicio.getDate() + diaIdx);
    const dow = dt.getDay();
    return dow === 0 || dow === 6;
  }

  const colWidth = view === "dia" ? 240 : view === "semana" ? 80 : 36;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
            <CalendarIcon size={22} className="text-blue-600 dark:text-blue-400" /> Cronograma
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1 text-sm">
            Marcos dos projetos e eventos da empresa no mesmo calendário. Use os filtros pra ver só o que interessa.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex bg-slate-100 dark:bg-slate-800 rounded-lg p-0.5" title="Modo de visualização">
            <button
              onClick={() => trocarModo("grade")}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md ${
                modo === "grade" ? "bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 shadow-sm" : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
              }`}
              title="Grade (calendário tradicional)"
            >
              <LayoutGrid size={12} /> Grade
            </button>
            <button
              onClick={() => trocarModo("linha")}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md ${
                modo === "linha" ? "bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 shadow-sm" : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
              }`}
              title="Linha do tempo (Gantt) — só marcos de projeto"
            >
              <Rows size={12} /> Linha
            </button>
          </div>
          <div className="inline-flex bg-slate-100 dark:bg-slate-800 rounded-lg p-0.5">
            {(["dia", "semana", "mes"] as View[]).map((v) => (
              <button
                key={v}
                onClick={() => setViewPersist(v)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md ${
                  view === v ? "bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 shadow-sm" : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                }`}
              >
                {v === "dia" ? "Dia" : v === "semana" ? "Semana" : "Mês"}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1.5">
            <button onClick={() => navegar(-1)} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200" title="Anterior">
              <ChevronLeft size={16} />
            </button>
            <span className="text-sm font-medium text-slate-800 dark:text-slate-100 capitalize px-3 min-w-[12rem] text-center">{label}</span>
            <button onClick={() => navegar(1)} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200" title="Próximo">
              <ChevronRight size={16} />
            </button>
            <button onClick={irParaHoje} className="ml-1 px-3 py-2 rounded-lg text-xs font-medium bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200">
              Hoje
            </button>
          </div>
          {podeGerenciar && (
            <>
              <button
                // Usa o dia de referência (o período que está na tela), não "hoje":
                // navegou pra setembro e clicou aqui, o modal abre em setembro.
                onClick={() => setCriando({ data: ymd(referencia) })}
                className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-sm font-medium"
              >
                <Plus size={14} /> Novo evento
              </button>
              <button
                onClick={() => setShowConfig(true)}
                className="flex items-center gap-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 px-3 py-2 rounded-lg text-sm font-medium"
                title="Configurações do calendário (aniversários e lembretes)"
              >
                <Settings size={14} /> Config
              </button>
            </>
          )}
        </div>
      </div>

      {/* Filtro único: projetos + eventos da empresa */}
      <div className="flex flex-wrap gap-1.5 items-center">
        <FiltroTipoChip ativo={!filtro} onClick={() => setFiltro("")}>Tudo</FiltroTipoChip>
        <FiltroTipoChip ativo={filtro === "projetos"} onClick={() => trocarFiltro("projetos")}>
          <Briefcase size={12} />
          <span>Projetos</span>
        </FiltroTipoChip>
        <span className="w-px h-4 bg-slate-200 dark:bg-slate-700 mx-0.5" />
        {TIPOS_LISTA.map((t) => (
          <FiltroTipoChip key={t} ativo={filtro === t} onClick={() => trocarFiltro(t)}>
            <span>{EVENTO_VISUAL[t].emoji}</span>
            <span>{EVENTO_VISUAL[t].label}</span>
          </FiltroTipoChip>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2.5 text-xs text-slate-600 dark:text-slate-400">
        <span className="text-slate-500 dark:text-slate-400 mr-1">Marcos do projeto:</span>
        <LegendaMarco emoji="▶" label="Início" />
        <LegendaMarco emoji="📤" label="1ª entrega" />
        <LegendaMarco emoji="⏳" label="Prazo feedback" />
        <LegendaMarco emoji="✅" label="Validado" />
        <LegendaMarco emoji="🎯" label="Entrega final" />
        <LegendaMarco emoji="🛟" label="Início suporte" />
        <LegendaMarco emoji="🏁" label="Fim suporte" />
        <span className="text-slate-400 dark:text-slate-500 ml-2">·</span>
        <span className="text-slate-500 dark:text-slate-400">↻ = data original era fim de semana</span>
      </div>

      {modo === "linha" && (
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-x-auto">
        <div style={{ minWidth: `${220 + colWidth * totalDias}px` }}>
          {/* Header dos dias */}
          <div className="grid border-b border-slate-200 dark:border-slate-700 sticky top-0" style={{ gridTemplateColumns: `220px repeat(${totalDias}, minmax(${colWidth}px, 1fr))` }}>
            <div className="px-3 py-2 text-xs font-medium text-slate-600 dark:text-slate-300 border-r border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">Projeto</div>
            {Array.from({ length: totalDias }, (_, i) => {
              const isWeekend = isWeekendColuna(i);
              const isHoje = isHojeColuna(i);
              return (
                <div
                  key={i}
                  className={`text-center text-[11px] py-2 border-r border-slate-100 dark:border-slate-700 capitalize ${isWeekend ? "bg-slate-200 dark:bg-slate-900/70 text-slate-400 dark:text-slate-500" : "bg-slate-50 dark:bg-slate-900/50 text-slate-600 dark:text-slate-400"} ${isHoje ? "!bg-blue-100 dark:!bg-blue-900/50 !text-blue-700 dark:!text-blue-300 font-bold" : ""}`}
                >
                  {tituloColuna(i)}
                </div>
              );
            })}
          </div>

          {/* Linhas dos projetos — marcos pontuais */}
          {isLoading ? (
            <div className="p-8 text-center text-slate-400 dark:text-slate-500 text-sm">Carregando projetos…</div>
          ) : projetosVisiveis.length === 0 ? (
            <div className="p-8 text-center text-slate-400 dark:text-slate-500 text-sm">Nenhum projeto neste período.</div>
          ) : (
            projetosVisiveis.map((p) => {
              const diaInicial = new Date(inicio.getFullYear(), inicio.getMonth(), inicio.getDate());
              const marcos = marcosDoProjeto(p)
                .map((mk) => {
                  const ajustada = ajustarParaDiaUtil(mk.data);
                  return { ...mk, dataExibicao: ajustada, ajustado: ajustada.getTime() !== mk.data.getTime() };
                })
                .filter((mk) => mk.dataExibicao >= inicio && mk.dataExibicao <= fim)
                .map((mk) => {
                  const offset = Math.floor((new Date(mk.dataExibicao.getFullYear(), mk.dataExibicao.getMonth(), mk.dataExibicao.getDate()).getTime() - diaInicial.getTime()) / 86400000);
                  return { ...mk, coluna: offset + 2 }; // +1 pelo grid 1-indexed, +1 pela primeira coluna do projeto
                });

              return (
                <div
                  key={p.id}
                  className="grid items-center border-b border-slate-100 dark:border-slate-700 hover:bg-slate-50/40 dark:hover:bg-slate-700/30 transition-colors relative"
                  style={{ gridTemplateColumns: `220px repeat(${totalDias}, minmax(${colWidth}px, 1fr))` }}
                >
                  <Link to={`/projetos/${p.id}`} className="px-3 py-3 border-r border-slate-200 dark:border-slate-700 min-w-0">
                    <p className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{p.nome}</p>
                    <p className="text-[10px] text-slate-500 dark:text-slate-400 truncate">{p.cliente}</p>
                  </Link>

                  {/* Fundo cinza nas colunas FDS */}
                  {Array.from({ length: totalDias }, (_, i) => {
                    if (!isWeekendColuna(i)) return null;
                    return (
                      <div
                        key={`fds-${i}`}
                        style={{ gridColumn: `${i + 2}`, gridRow: 1 }}
                        className="bg-slate-100/70 dark:bg-slate-900/40 h-full"
                      />
                    );
                  })}

                  {/* Marcos pontuais na linha */}
                  {marcos.length === 0 && (
                    <div
                      style={{ gridColumn: `2 / span ${totalDias}`, gridRow: 1 }}
                      className="text-[10px] text-slate-300 dark:text-slate-600 italic px-2 py-1"
                    >
                      sem marcos no período
                    </div>
                  )}
                  {marcos.map((mk, i) => (
                    <Link
                      key={`${mk.tipo}-${i}`}
                      to={`/projetos/${p.id}`}
                      style={{ gridColumn: `${mk.coluna} / span 1`, gridRow: 1 }}
                      className={`mx-0.5 my-1.5 rounded ${mk.cor} border text-[10px] font-medium px-1 py-0.5 flex items-center justify-center gap-0.5 hover:scale-105 transition-transform whitespace-nowrap`}
                      title={`${mk.label}: ${p.nome} · ${mk.data.toLocaleDateString("pt-BR")}${mk.ajustado ? " (movido pra próximo dia útil)" : ""}`}
                    >
                      <span>{mk.emoji}</span>
                      {mk.ajustado && <span className="opacity-60">↻</span>}
                    </Link>
                  ))}
                </div>
              );
            })
          )}
        </div>
      </div>
      )}

      {modo === "grade" && (
        <GradeCalendario
          dias={dias}
          isLoading={isLoading}
          referencia={referencia}
          view={view}
          hojeIso={hojeIso}
          marcosPorDia={marcosPorDia}
          eventosPorDia={eventosPorDia}
          podeGerenciar={podeGerenciar}
          onCriar={(dataYmd) => setCriando({ data: dataYmd })}
          onEditar={(ev) => setEditando(ev)}
          onVerDia={(d) => { setReferencia(d); setViewPersist("dia"); }}
        />
      )}

      {(criando || editando) && (
        <EventoModal
          inicial={editando ? editando : { data: criando!.data }}
          modoEdicao={!!editando}
          onClose={() => { setCriando(null); setEditando(null); }}
          onSucesso={() => {
            qc.invalidateQueries({ queryKey: ["calendario-eventos"] });
            setCriando(null);
            setEditando(null);
          }}
        />
      )}

      {showConfig && <ConfigCalendarioModal onClose={() => setShowConfig(false)} />}
    </div>
  );
}

function GradeCalendario({ dias, isLoading, referencia, view, hojeIso, marcosPorDia, eventosPorDia, podeGerenciar, onCriar, onEditar, onVerDia }: {
  dias: Date[];
  isLoading: boolean;
  referencia: Date;
  view: View;
  hojeIso: string;
  marcosPorDia: Map<string, MarcoExibicao[]>;
  eventosPorDia: Map<string, EventoCalendario[]>;
  podeGerenciar: boolean;
  onCriar: (dataYmd: string) => void;
  onEditar: (ev: EventoCalendario) => void;
  onVerDia: (dia: Date) => void;
}) {
  if (isLoading) {
    return <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-8 text-center text-slate-400 dark:text-slate-500 text-sm">Carregando…</div>;
  }

  // Modo Dia — lista vertical com eventos da empresa e marcos juntos
  if (view === "dia") {
    const dia = dias[0];
    const k = ymd(dia);
    const eventos = eventosPorDia.get(k) || [];
    const marcos = marcosPorDia.get(k) || [];
    const vazio = eventos.length === 0 && marcos.length === 0;
    return (
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
        <div className={`px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between ${k === hojeIso ? "bg-blue-50 dark:bg-blue-900/30" : "bg-slate-50 dark:bg-slate-900/50"}`}>
          <p className="text-sm font-medium text-slate-700 dark:text-slate-200 capitalize">
            {dia.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}
          </p>
          {podeGerenciar && (
            <button type="button" onClick={() => onCriar(k)} className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1">
              <Plus size={12} /> Novo evento neste dia
            </button>
          )}
        </div>
        <div className="p-4 space-y-2">
          {vazio && <p className="text-sm text-slate-400 dark:text-slate-500 text-center py-8">Nada neste dia.</p>}

          {eventos.map((ev, i) => {
            const visual = EVENTO_VISUAL[ev.tipo];
            const podeClicar = !!ev.id && podeGerenciar;
            return (
              <button
                type="button"
                key={`ev-${ev.id || ev.titulo}-${i}`}
                onClick={() => podeClicar && onEditar(ev)}
                className={`w-full text-left rounded-lg p-3 border ${visual.cor} hover:opacity-90 transition-opacity ${podeClicar ? "" : "cursor-default"}`}
              >
                <p className="text-sm font-semibold flex items-center gap-2 flex-wrap">
                  <span>{visual.emoji}</span>
                  {ev.hora && <span className="tabular-nums text-xs opacity-80">{ev.hora}</span>}
                  <span>{ev.titulo}</span>
                </p>
                {ev.descricao && <p className="text-xs opacity-80 mt-1">{ev.descricao}</p>}
                {ev.origem === "feriado-api" && <p className="text-[10px] opacity-60 mt-1">Feriado nacional (não editável)</p>}
              </button>
            );
          })}

          {marcos.map((mk, i) => (
            <Link
              key={`mk-${mk.projeto.id}-${mk.tipo}-${i}`}
              to={`/projetos/${mk.projeto.id}`}
              className={`block rounded-lg p-3 border ${mk.cor} hover:opacity-90 transition-opacity`}
              title={mk.ajustado ? `Data original: ${mk.data.toLocaleDateString("pt-BR")} (movido para próximo dia útil)` : undefined}
            >
              <p className="text-sm font-semibold flex items-center gap-2">
                <span>{mk.emoji}</span>
                <span>{mk.label}</span>
                {mk.ajustado && <span className="text-[10px] opacity-70">(de {mk.data.toLocaleDateString("pt-BR")})</span>}
              </p>
              <p className="text-xs opacity-80 mt-0.5">{mk.projeto.nome} · {mk.projeto.cliente}</p>
            </Link>
          ))}
        </div>
      </div>
    );
  }

  const headerDias = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
  const isMes = view === "mes";

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
      {/* Header dos dias */}
      <div className="grid grid-cols-7 border-b border-slate-200 dark:border-slate-700">
        {headerDias.map((d, i) => {
          const ehFds = i === 0 || i === 6;
          return (
            <div
              key={d}
              className={`px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wide ${
                ehFds ? "bg-slate-200 dark:bg-slate-900/70 text-slate-500 dark:text-slate-500" : "bg-slate-50 dark:bg-slate-900/50 text-slate-600 dark:text-slate-300"
              }`}
            >
              {d}
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-7">
        {dias.map((dia, idx) => {
          const k = ymd(dia);
          const ehHoje = k === hojeIso;
          const ehMesAtual = isMes ? dia.getMonth() === referencia.getMonth() : true;
          const dow = dia.getDay();
          const ehFds = dow === 0 || dow === 6;

          // Eventos da empresa vêm primeiro (têm hora); marcos de projeto depois.
          const eventos = eventosPorDia.get(k) || [];
          const marcos = marcosPorDia.get(k) || [];
          const limite = isMes ? 4 : 8;
          const evVisiveis = eventos.slice(0, limite);
          const mkVisiveis = marcos.slice(0, Math.max(0, limite - evVisiveis.length));
          const restantes = eventos.length + marcos.length - evVisiveis.length - mkVisiveis.length;

          // FDS sempre cinza (mesmo no mês atual). Outros meses ficam apagados.
          const bg =
            ehFds ? "bg-slate-100 dark:bg-slate-900/60" :
            ehMesAtual ? "bg-white dark:bg-slate-800" :
            "bg-slate-50/70 dark:bg-slate-900/40";

          return (
            <div
              key={idx}
              className={`border-b border-r border-slate-100 dark:border-slate-700 ${idx % 7 === 0 ? "border-l" : ""} ${
                isMes ? "min-h-[110px]" : "min-h-[260px]"
              } p-1.5 flex flex-col gap-0.5 ${bg} ${podeGerenciar ? "cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/30" : ""}`}
              onClick={() => { if (podeGerenciar) onCriar(k); }}
            >
              <div className="flex items-center justify-between px-1">
                <span
                  className={`text-[11px] font-medium inline-flex items-center justify-center ${
                    ehHoje
                      ? "bg-blue-600 text-white rounded-full w-6 h-6"
                      : ehFds
                        ? "text-slate-400 dark:text-slate-500"
                        : ehMesAtual
                          ? "text-slate-700 dark:text-slate-200"
                          : "text-slate-400 dark:text-slate-500"
                  }`}
                >
                  {dia.getDate()}
                </span>
              </div>
              <div className="flex flex-col gap-0.5 mt-0.5">
                {evVisiveis.map((ev, i) => {
                  const visual = EVENTO_VISUAL[ev.tipo];
                  const podeClicar = !!ev.id && podeGerenciar;
                  return (
                    <button
                      type="button"
                      key={`ev-${ev.id || ev.titulo}-${i}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (podeClicar) onEditar(ev);
                      }}
                      className={`text-[10px] px-1.5 py-0.5 rounded truncate font-medium border ${visual.cor} hover:opacity-90 transition-opacity flex items-center gap-1 text-left ${podeClicar ? "" : "cursor-default"}`}
                      title={`${visual.label}: ${ev.titulo}${ev.hora ? ` às ${ev.hora}` : ""}${ev.descricao ? ` — ${ev.descricao}` : ""}${ev.origem === "feriado-api" ? " (feriado nacional)" : ""}`}
                    >
                      <span className="shrink-0">{visual.emoji}</span>
                      {ev.hora && <span className="shrink-0 tabular-nums opacity-80">{ev.hora}</span>}
                      <span className="truncate">{ev.titulo}</span>
                    </button>
                  );
                })}
                {mkVisiveis.map((mk, i) => (
                  <Link
                    key={`mk-${mk.projeto.id}-${mk.tipo}-${i}`}
                    to={`/projetos/${mk.projeto.id}`}
                    onClick={(e) => e.stopPropagation()}
                    className={`text-[10px] px-1.5 py-0.5 rounded truncate font-medium border ${mk.cor} hover:opacity-90 transition-opacity flex items-center gap-1`}
                    title={
                      mk.ajustado
                        ? `${mk.label}: ${mk.projeto.nome} (data original ${mk.data.toLocaleDateString("pt-BR")} caiu em fim de semana)`
                        : `${mk.label}: ${mk.projeto.nome}`
                    }
                  >
                    <span className="shrink-0">{mk.emoji}</span>
                    <span className="truncate">{mk.projeto.nome}</span>
                    {mk.ajustado && <span className="opacity-60 shrink-0">↻</span>}
                  </Link>
                ))}
                {restantes > 0 && (
                  // Abre a view Dia: é a única forma de ver o que não coube — e num
                  // dia cheio de eventos os marcos de projeto ficam todos aqui dentro.
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onVerDia(dia); }}
                    className="text-[10px] px-1.5 py-0.5 text-slate-500 dark:text-slate-400 font-medium text-left hover:text-blue-600 dark:hover:text-blue-400 hover:underline"
                    title="Ver tudo deste dia"
                  >
                    +{restantes} mais
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LegendaMarco({ emoji, label }: { emoji: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span>{emoji}</span>
      <span>{label}</span>
    </span>
  );
}
